import React, { useState, useEffect } from 'react';
import { 
    Dialog, DialogTitle, DialogContent, DialogActions, 
    TextField, Button, Grid, FormControlLabel, Checkbox, Typography, Paper 
} from '@mui/material';
import { LoadingButton } from "@mui/lab";
import { createQuickCustomer } from '../../../controllers/company/subscriptions/SubscriptionsController';
// Asegúrate de que esta ruta sea correcta según tu estructura de carpetas


export default function QuickCustomerModal({ open, onClose, onCustomerCreated, role }) {
    // role puede ser: 'payer' (Pagador) o 'beneficiary' (Alumno)

    const [isLoading, setIsLoading] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        last_name: '',
        email: '',
        phone: ''
    });
    
    // Estado para el checkbox de crear usuario
    const [createUser, setCreateUser] = useState(false);
    
    // Errores de validación
    const [errors, setErrors] = useState({});

    /*
    |--------------------------------------------------------------------------
    | EFECTO DE CONFIGURACIÓN INICIAL
    |--------------------------------------------------------------------------
    | ES - Configura el formulario según el rol cuando se abre el modal.
    */
    useEffect(() => {
        if (open) {
            // Resetear formulario
            setFormData({ name: '', last_name: '', email: '', phone: '' });
            setErrors({});

            // Lógica del Checkbox según el Rol
            if (role === 'payer') {
                // Si es Pagador, OBLIGATORIAMENTE necesita usuario para pagar
                setCreateUser(true);
            } else {
                // Si es Alumno, es opcional (por defecto NO)
                setCreateUser(false);
            }
        }
    }, [open, role]);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
        // Limpiar error del campo al escribir
        if (errors[e.target.name]) {
            setErrors({ ...errors, [e.target.name]: false });
        }
    };

    const handleSubmit = async () => {
        // --- VALIDACIÓN LOCAL ---
        let newErrors = {};
        let hasError = false;

        if (!formData.name.trim()) { newErrors.name = true; hasError = true; }
        if (!formData.last_name.trim()) { newErrors.last_name = true; hasError = true; }
        
        // El email es obligatorio SOLO SI se va a crear un usuario
        if (createUser && !formData.email.trim()) { 
            newErrors.email = true; 
            hasError = true; 
        }

        if (hasError) {
            setErrors(newErrors);
            return;
        }

        // --- ENVÍO ---
        setIsLoading(true);
        
        // Preparamos los datos para el backend
        const payload = { 
            ...formData, 
            create_user: createUser ? 1 : 0 // Enviamos 1 o 0
        };
        
        const response = await createQuickCustomer(payload);
        
        setIsLoading(false);

        if (response.success) {
            // Notificamos al padre con el nuevo cliente creado
            onCustomerCreated(response.data); 
            onClose();
        } else {
            // Manejo de errores del backend
            if (response.data && response.data.email) {
                setErrors({ ...errors, email: true });
                alert("El correo electrónico ya está registrado.");
            } else {
                alert(response.message || "Error al crear el cliente.");
            }
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                {role === 'payer' ? 'Registrar Responsable de Pago' : 'Registrar Beneficiario'}
            </DialogTitle>
            
            <DialogContent>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                    {/* Nombre y Apellido */}
                    <Grid item xs={12} md={6}>
                        <TextField 
                            fullWidth label="Nombre" name="name" required
                            value={formData.name} onChange={handleChange} 
                            error={errors.name} helperText={errors.name && "Requerido"}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField 
                            fullWidth label="Apellido" name="last_name" required
                            value={formData.last_name} onChange={handleChange} 
                            error={errors.last_name} helperText={errors.last_name && "Requerido"}
                        />
                    </Grid>
                    
                    {/* Sección de Usuario / Login */}
                    <Grid item xs={12}>
                        <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f9f9f9', borderColor: createUser ? 'primary.main' : 'divider' }}>
                            <FormControlLabel
                                control={
                                    <Checkbox 
                                        checked={createUser}
                                        // Si es 'payer', deshabilitamos para que no pueda desmarcarlo
                                        disabled={role === 'payer'} 
                                        onChange={(e) => setCreateUser(e.target.checked)}
                                        color="primary"
                                    />
                                }
                                label={
                                    <Typography variant="body2" fontWeight="bold">
                                        Crear cuenta de usuario (Login)
                                    </Typography>
                                }
                            />
                            <Typography variant="caption" display="block" color="textSecondary" sx={{ ml: 4 }}>
                                {role === 'payer' 
                                    ? "Obligatorio. El responsable necesita usuario para gestionar pagos." 
                                    : "Opcional. Marca esto si el alumno necesita entrar a la App con su propio correo."}
                            </Typography>
                        </Paper>
                    </Grid>

                    {/* Email y Teléfono */}
                    <Grid item xs={12}>
                        <TextField 
                            fullWidth label="Email" name="email" type="email"
                            value={formData.email} onChange={handleChange}
                            error={errors.email}
                            // Mostramos el asterisco de requerido solo si createUser es true
                            required={createUser}
                            helperText={errors.email ? "Requerido para crear la cuenta de usuario" : ""}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField 
                            fullWidth label="Teléfono" name="phone" 
                            value={formData.phone} onChange={handleChange}
                        />
                    </Grid>
                </Grid>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} disabled={isLoading}>Cancelar</Button>
                <LoadingButton 
                    loading={isLoading} 
                    onClick={handleSubmit} 
                    variant="contained"
                >
                    Guardar y Seleccionar
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
}